import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore, type LoginConfig } from '@/stores/auth'
import { LoginCancelledError, loadTelegramLogin, requestTelegramLogin } from '@/telegramLogin'

// Where a finished login goes: the `?redirect=` the router guard put on the page (an in-app path only), else the todos.
export function redirectTarget(redirect: unknown): string {
  return typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/todos'
}

// "Log in with Telegram" for a plain browser, offered only when the backend is configured for it
// (`TELEGRAM_LOGIN_CLIENT_ID`). The library is loaded up front: a popup is only allowed straight from the click.
// Shared by the sign-in page and the landing page.
export function useBrowserLogin() {
  const { t, locale } = useI18n()
  const auth = useAuthStore()
  const route = useRoute()
  const router = useRouter()

  const loginConfig = ref<Extract<LoginConfig, { state: 'ready' }> | null>(null)
  const loginConfigState = ref<LoginConfig['state'] | null>(null)
  const loginReady = ref(false)
  const loginBusy = ref(false)
  const loginMessage = ref<string | null>(null)

  async function prepare(): Promise<void> {
    const config = await auth.fetchLoginConfig()
    loginConfigState.value = config.state
    loginConfig.value = config.state === 'ready' ? config : null
    if (!loginConfig.value) return
    try {
      await loadTelegramLogin()
      loginReady.value = true
    } catch {
      loginMessage.value = t('auth.loginUnavailable')
    }
  }

  async function login(): Promise<void> {
    const config = loginConfig.value
    if (!config || loginBusy.value) return
    loginBusy.value = true
    loginMessage.value = null
    try {
      const idToken = await requestTelegramLogin({
        clientId: config.clientId,
        nonce: config.nonce,
        lang: locale.value,
      })
      await auth.signInWithTelegramLogin(idToken, config.nonce)
      if (auth.status === 'authenticated') {
        await router.replace(redirectTarget(route.query.redirect))
        return
      }
      loginMessage.value = auth.errorMessage || t('auth.loginFailed')
      // (the nonce is only valid for a few minutes, a retry needs a fresh one)
      const fresh = await auth.fetchLoginConfig()
      loginConfig.value = fresh.state === 'ready' ? fresh : config
    } catch (e) {
      loginMessage.value =
        e instanceof LoginCancelledError ? t('auth.loginClosed') : t('auth.loginFailed')
    } finally {
      loginBusy.value = false
    }
  }

  return { loginConfig, loginConfigState, loginReady, loginBusy, loginMessage, prepare, login }
}
